import { Injectable, OnDestroy } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { SessionService } from '@services/index.service';
import { ErrorCode } from '@models/index.model';

/**
 * Mantiene viva la sesión de la app en segundo plano, sin interacción del
 * usuario, mientras el refreshToken siga siendo válido. Solo desloguea cuando
 * el propio refreshToken deja de ser válido.
 *
 * Arrancar una única vez al iniciar la app (ver AppComponent).
 */
@Injectable()
export class SessionKeepAliveService implements OnDestroy {
  /** Margen antes de la expiración para refrescar (ms). */
  private readonly _skewMs = 60_000;
  /** Retraso mínimo por si la expiración viene muy justa (ms). */
  private readonly _minDelayMs = 5_000;

  private _timerSub: Subscription | null = null;
  private _loggedInSub: Subscription | null = null;
  private readonly _visibilityHandler = () => this._onVisible();

  constructor(private readonly _session: SessionService) {}

  /** Llamar UNA vez al arrancar la app. */
  public start(): void {
    this.stop();

    // Reprograma/limpia según login-logout.
    this._loggedInSub = this._session.loggedIn$().subscribe((logged) => {
      if (logged) this._schedule();
      else this._clearTimer();
    });

    // Al volver a la pestaña (típico tras el redirect de SharePoint/MSAL)
    // revalidamos de inmediato.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }

    // Si ya había sesión al cargar la app.
    if (this._session.isLoggedIn()) this._schedule();
  }

  public stop(): void {
    this._clearTimer();
    this._loggedInSub?.unsubscribe();
    this._loggedInSub = null;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /** Programa el próximo refresco un poco antes de la expiración. */
  private _schedule(): void {
    this._clearTimer();

    const exp = this._session.getExpiration();
    if (!exp || !this._session.hasRefreshToken()) return;

    const msToExp = exp.getTime() - Date.now();

    // Margen efectivo: nunca mayor que la mitad de la vida restante. Con tokens
    // de vida corta (aquí 5 min) esto evita programar el refresco dentro de una
    // ventana en la que no se llega a refrescar y se reprograma en bucle.
    const effectiveSkew = Math.min(this._skewMs, Math.max(Math.floor(msToExp / 2), 0));
    const delay = Math.max(msToExp - effectiveSkew, this._minDelayMs);

    // eslint-disable-next-line no-console
    // console.debug(`[refresh-debug] SessionKeepAliveService programa próximo refresh en ${delay}ms (msToExp=${msToExp}ms)`);

    this._timerSub = timer(delay).subscribe(() => this._refreshNow());
  }

  /** Refresca ya; reprograma en éxito y desloguea solo si el refreshToken es inválido. */
  private _refreshNow(): void {
    // Si la pestaña está oculta, no gastamos el refresh; al volver, visibilitychange lo hará.
    if (typeof document !== 'undefined' && document.hidden) return;
    if (!this._session.hasRefreshToken()) return;

    // Si otro flujo (actividad del usuario) ya refrescó, no rotamos de nuevo:
    // simplemente reprogramamos para la nueva expiración.
    if (!this._session.isAccessTokenExpiring(this._skewMs)) {
      this._schedule();
      return;
    }

    // skewMs: alinea el criterio de "expira pronto" del keep-alive con el de la
    // coalescencia de SessionService. Sin esto, el keep-alive despierta con un
    // margen (60s) que la coalescencia considera "aún fresco" (30s), devuelve la
    // sesión actual sin refrescar y el keep-alive se reprograma cada pocos
    // segundos sin llegar nunca a refrescar.
    this._session.refreshSessionShared({ skewMs: this._skewMs, source: 'SessionKeepAliveService' }).subscribe({
      next: () => this._schedule(),
      error: (err) => {
        const unauthorized = err?.status === 401 && err?.error?.errorCode === ErrorCode.UNAUTHORIZED;
        if (unauthorized) {
          // El refreshToken ya no vale: única condición para desloguear.
          this._session.removeBrowserSession();
        } else {
          // Error transitorio (red, 5xx...): reprograma para reintentar.
          this._schedule();
        }
      },
    });
  }

  private _onVisible(): void {
    if (typeof document !== 'undefined' && document.hidden) return;
    if (!this._session.hasRefreshToken()) return;

    if (this._session.isAccessTokenExpiring(this._skewMs)) {
      this._refreshNow();
    } else {
      this._schedule();
    }
  }

  private _clearTimer(): void {
    this._timerSub?.unsubscribe();
    this._timerSub = null;
  }
}
